import { useModalStore } from '@/stores/modalStore';
import { useSidebarStore } from '@/stores/sidebarStore';

/**
 * Run `run` once the overlay layer above us has finished unwinding its history
 * entry — the next `popstate`, bounded by a short fallback so we never hang.
 */
function afterUnwind(run: () => void): void {
  let settled = false;
  const done = (): void => {
    if (settled) return;
    settled = true;
    window.removeEventListener('popstate', done);
    run();
  };
  window.addEventListener('popstate', done);
  setTimeout(done, 300);
}

/**
 * Defer a navigation until a closing modal has finished unwinding history.
 *
 * A dialogue pushes its own history entry and pops it on close
 * (`useHistoryIntercept`). Anything that navigates in the same breath — opening a
 * column, restoring a parked draft — races that `history.back()` and gets
 * reverted, so the click appears to do nothing at all.
 *
 * `closeModal` flags the modal `closing` synchronously (it is spliced out ~200ms
 * later) and fires its close subscription synchronously, so at the moment a
 * follow-up call runs the closing modal is still on the stack. When one is present
 * we wait for its unwind `popstate` — bounded by a short fallback so we never hang
 * — before navigating; otherwise we navigate immediately, at no latency cost.
 */
export function runAfterModalUnwind(run: () => void): void {
  // An open peek counts too: it closes on a click outside it, but its auto-hide runs
  // AFTER the click's own handler — so a row picked beside a peek navigates first and
  // the peek's unwind then reverts it, and the first click seems to do nothing. If
  // the peek stays open after all, the fallback runs the navigation regardless.
  const modalIsUnwinding = useModalStore().modalStack.some((m) => m.closing || !!m.anchorEl);
  if (!modalIsUnwinding || typeof window === 'undefined') {
    run();
    return;
  }
  afterUnwind(run);
}

/**
 * The same deferral for a closing overlay SIDEBAR, which pushes and pops a
 * history entry of its own exactly like a modal does.
 *
 * This is what a panel whose primary action leaves the page needs: `closeSidebar`
 * flags the panel `closing` synchronously, and `useHistoryIntercept`'s watcher
 * then calls `history.back()` on the next flush. A `router.push` issued in the
 * same handler is still in flight at that point, so the pop CANCELS it
 * (`NavigationFailureType.cancelled`) — the panel slides away and the app stays
 * exactly where it was. Close first, navigate once the pop has landed.
 *
 * Inline cascade columns are mounted from the URL and own no intercept entry, so
 * they are not an unwind to wait for.
 */
export function runAfterSidebarUnwind(run: () => void): void {
  const sidebarIsClosing = useSidebarStore().sidebarStack.some((s) => s.closing && !s.inline);
  if (!sidebarIsClosing || typeof window === 'undefined') {
    run();
    return;
  }
  afterUnwind(run);
}
